import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, Baby, Calendar, CalendarPlus, Clock, Download, Euro, Mail, RefreshCw, Search, ShoppingBag, Sparkles, X,
} from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { formatDate, toCsv } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';

/**
 * Ventas de servicios pagados por el huésped en el portal.
 *
 * Los documentos son los de `service_purchases` de la base `guest_portal`, que
 * el CRM lee tal cual a través de `/api/guest-portal/service-sales`. Esta página
 * no escribe nada ni corrige nada: el dueño de estos datos es el portal, y el
 * cobro de verdad está en Stripe. Si un importe no cuadra, se mira allí.
 *
 * Copia del tipo del portal (`guest_portal/src/lib/cosmos.ts`), no importada:
 * son dos repos y dos despliegues. Si el portal cambia estos campos, el cambio
 * se trae a mano aquí y a `server/src/db/guest-portal.ts`.
 */
interface ServicePurchase {
  /** El id de la sesión de Stripe. Es la clave de idempotencia. */
  id: string;
  reservationId: string;
  listingId: string;
  serviceType: string;
  /** En céntimos, como lo entrega Stripe. */
  amountTotal: number;
  currency: string;
  /** Correo que el comprador teclea en la página de pago de Stripe. */
  email: string;
  /** Titular de la tarjeta, cuando Stripe lo captura. */
  buyerName: string;
  confirmationCode: string;
  listingNickname: string;
  checkIn: string;
  checkOut: string;
  locale: string;
  /**
   * Solo en los late checkout: la hora de salida comprada. Es lo que explica
   * que dos ventas del mismo servicio tengan importes distintos —se cobra por
   * horas sobre las 11:00—, así que se enseña junto al nombre del servicio.
   */
  lateCheckoutHour?: number;
  /** Solo en las ampliaciones: noches compradas y nueva fecha de salida. */
  extendNights?: number;
  extendCheckOut?: string;
  /**
   * Cuándo se devolvió el dinero. El portal lo escribe cuando cobra una
   * ampliación que luego no puede entregar —las noches se ocuparon mientras el
   * huésped pagaba— y la reembolsa sola. Una fila con esto no es un ingreso:
   * el dinero volvió.
   */
  refundedAt?: string;
  createdAt: string;
  notifiedAt?: string;
  emailedAt?: string;
  lastError?: string;
  /**
   * Cómo entró la compra. `stripe_backfill` marca las que se importaron de
   * Stripe. Es opcional: los documentos anteriores al campo no lo llevan y son
   * todos del webhook, así que la ausencia se lee como venta normal y nunca al
   * revés.
   */
  origin?: 'webhook' | 'stripe_backfill';
}

/** Los servicios que el huésped paga en el acto, con el nombre con el que los ve. */
const SERVICE_TYPES = [
  { value: 'portable_crib', label: 'Cuna Portátil', icon: Baby },
  { value: 'clean_towels', label: 'Pack de Toallas Limpias', icon: Sparkles },
  { value: 'late_checkout', label: 'Late Checkout', icon: Clock },
  { value: 'extend_stay', label: 'Ampliación de estancia', icon: CalendarPlus },
] as const;

const SERVICE_LABELS: Record<string, string> = Object.fromEntries(
  SERVICE_TYPES.map(s => [s.value, s.label])
);

/** Un servicio que no esté en el catálogo se muestra con su identificador, no en blanco. */
const serviceLabel = (serviceType: string) => SERVICE_LABELS[serviceType] || serviceType;

const serviceIcon = (serviceType: string) =>
  SERVICE_TYPES.find(s => s.value === serviceType)?.icon || ShoppingBag;

/**
 * Lo que distingue a dos ventas del mismo servicio. Hoy solo el late checkout
 * lo tiene: sin la hora, dos filas de «Late Checkout» a 15€ y a 30€ parecen un
 * error de cobro en vez de dos horas distintas.
 */
const serviceDetail = (
  p: Pick<ServicePurchase, 'serviceType' | 'lateCheckoutHour' | 'extendNights' | 'extendCheckOut'>
) => {
  if (p.serviceType === 'late_checkout' && p.lateCheckoutHour) {
    return `salida ${String(p.lateCheckoutHour).padStart(2, '0')}:00`;
  }
  if (p.serviceType === 'extend_stay' && p.extendNights) {
    const noches = `${p.extendNights} ${p.extendNights === 1 ? 'noche' : 'noches'}`;
    return p.extendCheckOut ? `${noches} → ${p.extendCheckOut}` : noches;
  }
  return '';
};

/** Una compra reembolsada no cuenta como venta: el dinero volvió al huésped. */
const isRefunded = (p: Pick<ServicePurchase, 'refundedAt'>) => Boolean(p.refundedAt);

/** Stripe devuelve la divisa en minúsculas ('eur'); Intl la exige en ISO 4217. */
function normalizeCurrency(currency?: string): string {
  const code = (currency || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : 'EUR';
}

/**
 * Los importes llegan en céntimos, enteros, como los entrega Stripe. Se suman
 * en céntimos y solo se dividen al imprimirlos: sumar euros en coma flotante es
 * lo que hace que diez ventas de 20,00 € acaben dando 199,99 €.
 */
function fmtMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

const fmtCount = (n: number) => `${n} ${n === 1 ? 'venta' : 'ventas'}`;

/**
 * La ausencia de `origin` se lee como venta del portal, no como importación:
 * el campo se añadió después y los documentos anteriores son todos del webhook.
 */
const isImported = (p: ServicePurchase) => p.origin === 'stripe_backfill';

/**
 * Una venta del portal sin correo de confirmación es un huésped que pagó y no
 * recibió justificante. Se marca sin ruido: no es un problema contable, pero
 * conviene que se vea. Las importadas quedan fuera a propósito —nunca se
 * intentó avisar a nadie, así que la falta de correo no dice nada.
 */
const needsReview = (p: ServicePurchase) => !isImported(p) && (!p.emailedAt || !!p.lastError);

const reviewReason = (p: ServicePurchase) =>
  p.lastError
    ? `El aviso de esta venta falló: ${p.lastError}`
    : 'Sin correo de confirmación registrado';


export default function ServiciosPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // El filtrado lo hace el backend contra Cosmos, así que cada tecla sería una
  // consulta —y unas RU— de más. 300 ms bastan para que solo salga la búsqueda
  // que el usuario ha terminado de escribir.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const params: Record<string, string> = {};
  if (serviceType) params.serviceType = serviceType;
  if (from) params.from = from;
  if (to) params.to = to;
  if (debouncedSearch) params.search = debouncedSearch;

  const { data: sales, loading, error, refresh } = useApi<ServicePurchase[]>(
    () => api.getGuestPortalServiceSales(Object.keys(params).length > 0 ? params : undefined),
    [serviceType, from, to, debouncedSearch]
  );

  const hasFilters = Boolean(serviceType || from || to || debouncedSearch);

  const clearFilters = () => {
    setSearch('');
    setServiceType('');
    setFrom('');
    setTo('');
  };

  /**
   * Totales de lo que hay en pantalla, no de todo lo cobrado: con un filtro
   * puesto, un total que no lo respetara sería un número que no cuadra con
   * ninguna de sus filas.
   *
   * Se agrupa por divisa además de por servicio. Hoy todo es EUR, pero sumar
   * divisas distintas en una sola cifra es la manera silenciosa de dar un total
   * falso el día que aparezca la primera venta en otra.
   */
  const summary = useMemo(() => {
    const rows = sales || [];
    const totals = new Map<string, number>();
    const perService = new Map<
      string,
      { serviceType: string; currency: string; count: number; cents: number }
    >();
    let imported = 0;

    let refunded = 0;

    for (const p of rows) {
      const currency = normalizeCurrency(p.currency);
      const cents = Math.round(Number(p.amountTotal) || 0);

      // Lo reembolsado se cuenta aparte y NO suma al importe total: es un cobro
      // que volvió al huésped porque no se pudo entregar lo que compró.
      if (isRefunded(p)) {
        refunded += 1;
        continue;
      }

      totals.set(currency, (totals.get(currency) || 0) + cents);

      const key = `${p.serviceType}|${currency}`;
      const acc = perService.get(key) || { serviceType: p.serviceType, currency, count: 0, cents: 0 };
      acc.count += 1;
      acc.cents += cents;
      perService.set(key, acc);

      if (isImported(p)) imported += 1;
    }

    // Los servicios del catálogo se listan siempre, aunque no se haya vendido
    // ninguno: «cero packs de toallas» es un dato, y una fila que falta no se
    // distingue de un dato que nadie ha mirado.
    const mainCurrency = [...totals.keys()][0] || 'EUR';
    const breakdown = [
      ...SERVICE_TYPES.map(
        s =>
          perService.get(`${s.value}|${mainCurrency}`) || {
            serviceType: s.value,
            currency: mainCurrency,
            count: 0,
            cents: 0,
          }
      ),
      // Lo que no cabe en el catálogo × divisa principal: servicios retirados o
      // ventas en otra divisa. Se enseñan igual, no se pierden.
      ...[...perService.values()].filter(
        r => !SERVICE_LABELS[r.serviceType] || r.currency !== mainCurrency
      ),
    ];

    return {
      count: rows.length - refunded,
      totals: [...totals.entries()],
      breakdown,
      imported,
      refunded,
    };
  }, [sales]);

  const exportCsv = () => {
    const rows = sales || [];
    if (rows.length === 0) return;

    const header = [
      'Fecha', 'Servicio', 'Importe', 'Divisa', 'Comprador', 'Email',
      'Reserva', 'ID reserva', 'Alojamiento', 'Entrada', 'Salida',
      'Origen', 'ID de pago (Stripe)',
    ];

    const lines = rows.map(p => [
      formatDate(p.createdAt),
      [serviceLabel(p.serviceType), serviceDetail(p)].filter(Boolean).join(' · '),
      // Número pelado, sin símbolo ni separador de miles: la gestoría necesita
      // una cifra que su programa pueda sumar, no un texto con formato.
      ((Math.round(Number(p.amountTotal) || 0)) / 100).toFixed(2).replace('.', ','),
      normalizeCurrency(p.currency),
      p.buyerName || '',
      p.email || '',
      p.confirmationCode || '',
      p.reservationId || '',
      p.listingNickname || '',
      formatDate(p.checkIn),
      formatDate(p.checkOut),
      isImported(p) ? 'Importada de Stripe' : 'Portal',
      p.id,
    ]);

    const csv = toCsv([header, ...lines]);

    const periodo = from || to ? `${from || 'inicio'}_${to || 'hoy'}` : new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ventas-servicios-portal_${periodo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Ventas de servicios</h1>
          <p className="text-sm text-sereno-dark/40 mt-0.5">
            Servicios que los huéspedes pagan en el portal · datos del portal, solo lectura
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} className="btn-secondary flex items-center gap-2 text-sm">
            <RefreshCw size={16} />
            <span className="hidden sm:inline">Actualizar</span>
          </button>
          <button
            onClick={exportCsv}
            disabled={!sales || sales.length === 0}
            className="btn-primary flex items-center gap-2 text-sm"
            title="Descargar en CSV las ventas que se están mostrando"
          >
            <Download size={16} />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input
              type="text"
              placeholder="Buscar por email, comprador, código de reserva o alojamiento..."
              className="input pl-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input w-auto"
            value={serviceType}
            onChange={e => setServiceType(e.target.value)}
          >
            <option value="">Todos los servicios</option>
            {SERVICE_TYPES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-100">
          <Calendar size={16} className="text-sereno-dark/40 flex-shrink-0" />
          <span className="text-sm text-sereno-dark/50 flex-shrink-0">Desde</span>
          <input
            type="date"
            className="input w-auto text-sm"
            value={from}
            max={to || undefined}
            onChange={e => setFrom(e.target.value)}
          />
          <span className="text-sm text-sereno-dark/50 flex-shrink-0">Hasta</span>
          <input
            type="date"
            className="input w-auto text-sm"
            value={to}
            min={from || undefined}
            onChange={e => setTo(e.target.value)}
          />
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
            >
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Totales — de lo que hay en pantalla */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-600 w-fit mb-2">
            <Euro size={20} />
          </div>
          <p className="text-xs text-sereno-dark/50">
            Importe total{hasFilters ? ' (filtrado)' : ''}
          </p>
          {summary.totals.length === 0 ? (
            <p className="text-lg font-bold tabular-nums">{fmtMoney(0, 'EUR')}</p>
          ) : (
            summary.totals.map(([currency, cents]) => (
              <p key={currency} className="text-lg font-bold tabular-nums">
                {fmtMoney(cents, currency)}
              </p>
            ))
          )}
        </div>

        <div className="card">
          <div className="p-2.5 rounded-lg bg-sky-50 text-sky-600 w-fit mb-2">
            <ShoppingBag size={20} />
          </div>
          <p className="text-xs text-sereno-dark/50">
            Ventas{hasFilters ? ' (filtradas)' : ''}
          </p>
          <p className="text-lg font-bold tabular-nums">{summary.count}</p>
        </div>

        <div className="card sm:col-span-2">
          <p className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider mb-3">
            Desglose por servicio
          </p>
          <table className="w-full text-sm">
            <tbody>
              {summary.breakdown.map(row => {
                const Icon = serviceIcon(row.serviceType);
                return (
                  <tr
                    key={`${row.serviceType}|${row.currency}`}
                    className={row.count === 0 ? 'opacity-40' : ''}
                  >
                    <td className="py-1.5">
                      <span className="flex items-center gap-2">
                        <Icon size={14} className="text-sereno-dark/40 flex-shrink-0" />
                        <span className="truncate">{serviceLabel(row.serviceType)}</span>
                      </span>
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-sereno-dark/50 whitespace-nowrap w-28">
                      {fmtCount(row.count)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums font-medium whitespace-nowrap w-28">
                      {fmtMoney(row.cents, row.currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resultados */}
      {error ? (
        <div className="card border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-700">Error al cargar las ventas de servicios</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <button onClick={refresh} className="btn-secondary text-sm mt-3 flex items-center gap-2">
              <RefreshCw size={14} />
              Reintentar
            </button>
          </div>
        </div>
      ) : loading ? (
        <LoadingSpinner />
      ) : !sales || sales.length === 0 ? (
        <EmptyState
          title="No hay ventas de servicios"
          description={
            hasFilters
              ? 'Ninguna venta encaja con los filtros aplicados.'
              : 'Aquí aparecerán las cunas y los packs de toallas que los huéspedes paguen en el portal.'
          }
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          {/* Tabla — escritorio */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Fecha</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Servicio</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Importe</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Comprador</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Reserva</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">Alojamiento</th>
                </tr>
              </thead>
              <tbody>
                {sales.map(p => {
                  const Icon = serviceIcon(p.serviceType);
                  return (
                    <tr key={p.id} className="border-b border-gray-50 last:border-0 hover:bg-sereno-blue/10 transition-colors">
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sereno-dark/70">{formatDate(p.createdAt)}</span>
                          {isRefunded(p) && (
                            <span
                              className="text-[10px] uppercase tracking-wide text-red-600 border border-red-200 rounded px-1 py-0.5"
                              title="Cobrada y reembolsada: el portal no pudo confirmar las noches y devolvió el importe"
                            >
                              reembolsada
                            </span>
                          )}
                          {isImported(p) && (
                            <span
                              className="text-[10px] uppercase tracking-wide text-sereno-dark/35 border border-gray-200 rounded px-1 py-0.5"
                              title="Importada de Stripe: se cobró antes de que el portal registrara la venta, así que no tiene ni aviso ni correo"
                            >
                              importada
                            </span>
                          )}
                          {needsReview(p) && (
                            <span className="flex items-center text-amber-500" title={reviewReason(p)}>
                              <AlertCircle size={13} />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="flex items-center gap-2 text-sereno-dark whitespace-nowrap">
                          <Icon size={14} className="text-sereno-dark/40 flex-shrink-0" />
                          {serviceLabel(p.serviceType)}
                          {serviceDetail(p) && (
                            <span className="text-[11px] text-sereno-dark/45">{serviceDetail(p)}</span>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right font-medium tabular-nums text-sereno-dark whitespace-nowrap">
                        {fmtMoney(p.amountTotal, normalizeCurrency(p.currency))}
                      </td>
                      <td className="px-5 py-3">
                        <p className="font-medium text-sereno-dark truncate max-w-[200px]">
                          {p.buyerName || <span className="font-normal text-sereno-dark/25">Sin nombre</span>}
                        </p>
                        {p.email && (
                          <a
                            href={`mailto:${p.email}`}
                            className="flex items-center gap-1 text-[11px] text-sereno-dark/50 hover:text-sereno-dark truncate max-w-[200px]"
                          >
                            <Mail size={11} className="flex-shrink-0" /> {p.email}
                          </a>
                        )}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <p className="font-mono text-xs text-sereno-dark">{p.confirmationCode || '—'}</p>
                        <p className="text-[11px] text-sereno-dark/35">ID {p.reservationId}</p>
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-sereno-dark/70 truncate max-w-[220px]">{p.listingNickname || '—'}</p>
                        <p className="text-[11px] text-sereno-dark/35 whitespace-nowrap">
                          {formatDate(p.checkIn)} → {formatDate(p.checkOut)}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {summary.totals.map(([currency, cents]) => (
                  <tr key={currency} className="bg-gray-50 border-t border-gray-200">
                    <td colSpan={2} className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-sereno-dark/50">
                      {summary.totals.length > 1 ? `Total en ${currency}` : `Total · ${fmtCount(summary.count)}`}
                    </td>
                    <td className="px-5 py-3 text-right font-bold tabular-nums text-sereno-dark whitespace-nowrap">
                      {fmtMoney(cents, currency)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>

          {/* Fichas — móvil y tableta */}
          <div className="lg:hidden divide-y divide-gray-50">
            {sales.map(p => {
              const Icon = serviceIcon(p.serviceType);
              return (
                <div key={p.id} className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon size={15} className="text-sereno-dark/40 flex-shrink-0" />
                    <span className="font-medium text-sm text-sereno-dark">{serviceLabel(p.serviceType)}</span>
                    {serviceDetail(p) && (
                      <span className="text-[11px] text-sereno-dark/45 whitespace-nowrap">{serviceDetail(p)}</span>
                    )}
                    <span className="ml-auto font-medium tabular-nums text-sereno-dark whitespace-nowrap">
                      {fmtMoney(p.amountTotal, normalizeCurrency(p.currency))}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-sereno-dark/40">
                    <span>{formatDate(p.createdAt)}</span>
                    {isImported(p) && (
                      <span
                        className="text-[10px] uppercase tracking-wide text-sereno-dark/35 border border-gray-200 rounded px-1 py-0.5"
                        title="Importada de Stripe: se cobró antes de que el portal registrara la venta, así que no tiene ni aviso ni correo"
                      >
                        importada
                      </span>
                    )}
                    {needsReview(p) && (
                      <span className="flex items-center gap-1 text-amber-600" title={reviewReason(p)}>
                        {/* El webhook anota el fallo de Telegram ANTES de enviar el
                            correo y `markServicePurchase` no lo limpia después, así que
                            una venta puede tener `emailedAt` y `lastError` a la vez:
                            rotularla siempre «sin correo» contradiría a su propio tooltip. */}
                        <AlertCircle size={12} /> {p.lastError ? 'con incidencia' : 'sin correo'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-sereno-dark">
                    {p.buyerName || <span className="text-sereno-dark/25">Sin nombre</span>}
                  </div>
                  {p.email && (
                    <a href={`mailto:${p.email}`} className="flex items-center gap-1 text-xs text-sereno-dark/50 truncate">
                      <Mail size={12} className="flex-shrink-0" /> {p.email}
                    </a>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-sereno-dark/50">
                    <span className="font-mono">{p.confirmationCode || '—'}</span>
                    <span>{p.listingNickname || '—'}</span>
                    <span className="text-sereno-dark/35">
                      {formatDate(p.checkIn)} → {formatDate(p.checkOut)}
                    </span>
                  </div>
                </div>
              );
            })}
            {summary.totals.map(([currency, cents]) => (
              <div key={currency} className="flex items-center justify-between px-4 py-3 bg-gray-50">
                <span className="text-xs font-semibold uppercase tracking-wider text-sereno-dark/50">
                  {summary.totals.length > 1 ? `Total en ${currency}` : `Total · ${fmtCount(summary.count)}`}
                </span>
                <span className="font-bold tabular-nums text-sereno-dark">{fmtMoney(cents, currency)}</span>
              </div>
            ))}
          </div>

          {summary.imported > 0 && (
            <p className="px-5 py-3 border-t border-gray-100 text-[11px] text-sereno-dark/35">
              {summary.imported === 1
                ? 'Una de estas ventas está marcada como importada: se cobró'
                : `${summary.imported} de estas ventas están marcadas como importadas: se cobraron`}{' '}
              en Stripe antes de que el portal registrara la venta, así que no tienen ni aviso interno ni
              correo de confirmación. El cobro es igual de válido que el de las demás.
            </p>
          )}

          {summary.refunded > 0 && (
            <p className="px-5 py-3 border-t border-gray-100 text-[11px] text-sereno-dark/35">
              {summary.refunded === 1
                ? 'Una de estas filas se reembolsó automáticamente'
                : `${summary.refunded} de estas filas se reembolsaron automáticamente`}{' '}
              porque el portal cobró una ampliación que después no pudo confirmar (las noches se
              ocuparon mientras el huésped pagaba). No cuentan en el importe total ni en el número de
              ventas: ese dinero volvió al huésped.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
